import { ComponentKewlox } from './ComponentKewlox';
import {
  BattensEntity,
  PropsComponent,
  PropsGeneral,
  SearchInfo,
} from '../Data';
import { BATTEN_DEPTH, BATTEN_WIDTH, Material } from '../constants';
import { CustomPiece } from './CustomPiece';
import { computeWeightWithDensity, getPropsKey } from '../utils/helpers';
import { getPiecesProps } from '../utils/dataStorage';

export class Batten extends ComponentKewlox {
  readonly material: Material;
  readonly feet: boolean;

  static _lookup = ['height'];

  constructor(
    id: number,
    name: string,
    reference: string,
    price: number,
    weight: number,
    forSales: boolean,
    visible: boolean,
    height: number,
    material: Material,
    feet = false,
  ) {
    super(id, name, reference, price, weight, forSales, visible, height);
    this.material = material;
    this.feet = feet;
  }

  get typeComponent() {
    return 'Batten';
  }

  static fromJSON(json: BattensEntity): Batten {
    const batten = Object.create(Batten.prototype);

    return Object.assign(batten, this, json);
  }

  static reviver(key: string, value: any): any {
    return key === '' ? Batten.fromJSON(value) : value;
  }

  compareTo(otherComponent: ComponentKewlox): number {
    return this.height - otherComponent.height;
  }

  includeInto(otherComponent: ComponentKewlox): boolean {
    return this.height - otherComponent.height >= 0;
  }

  getKey(): string {
    return Batten.generateKey(this.height, this.material, this.feet);
  }

  static getSearchInfos(
    height: number,
    material: Material,
    feet = false,
  ): SearchInfo {
    return {
      lookup: Batten._lookup,
      key: { height, material, feet },
    };
  }

  static generateKey(height: number, material: Material, feet: boolean) {
    const searchInfos = Batten.getSearchInfos(height, material, feet);
    return JSON.stringify(searchInfos.key);
  }

  get realHeight() {
    return Batten.computeRealHeight(this.height);
  }

  get realWidth() {
    return Batten.computeRealWidth(BATTEN_WIDTH);
  }

  get realDepth() {
    return Batten.computeRealDepth(BATTEN_DEPTH);
  }

  static getProperties(material: Material): PropsComponent | void {
    const key = getPropsKey(material);

    if (getPiecesProps().batten.hasOwnProperty(key)) {
      return getPiecesProps().batten[key];
    }
  }

  static computeBasePrice(
    material: Material,
    height: number,
    props: PropsComponent,
    propsGen: PropsGeneral,
  ) {
    /**
     * This formula came from the PRO100 Excel linked file
     *
     * Wood:
     * ((K1 * K2 * volume) + K3) * K4
     *
     * Alu:
     * ((height * K1) + K2) * K3
     *
     * height and volume in µ
     *
     */
    const heightMicr = height / 1000;
    const widthMicr = BATTEN_WIDTH / 1000;
    const depthMicr = BATTEN_DEPTH / 1000;

    const volume = heightMicr * widthMicr * depthMicr;

    const k1 = props.k1 ?? 0;
    const k2 = props.k2 ?? 0;
    const k3 = props.k3 ?? 0;
    const k4 = props.k4 ?? 0;

    let price = (volume * k1 * k4 + k2) * k3;
    if (
      material === Material.Aluminium ||
      material === Material.Black ||
      material === Material.White
    )
      price = (heightMicr * k1 + k2) * k3;

    price *= propsGen.coef;
    price = Math.round(price * 100) / 100;

    return price;
  }

  public computePrice(customPiece: CustomPiece<ComponentKewlox>): number {
    const props: PropsComponent | void = Batten.getProperties(this.material);
    const propsGen: PropsGeneral = getPiecesProps().general;

    if (!props) return 0;

    // @ts-ignore
    const material: Material = customPiece.base.material;
    const priceForDimensions = Batten.computeBasePrice(
      material,
      customPiece.realHeight,
      props,
      propsGen,
    );

    // @ts-ignore
    if (customPiece.custom && customPiece.base.material === Material.Wood) {
      // @ts-ignore
      const suppl = customPiece.base.feet
        ? propsGen.woodFeetBattenSuppl
        : propsGen.woodBattenSuppl;
      return Math.round(priceForDimensions + suppl * propsGen.coef * 100) / 100;
    }

    return priceForDimensions;
  }

  public computeWeight(customPiece: CustomPiece<ComponentKewlox>): number {
    // @ts-ignore
    const material: Material = customPiece.base.material;
    const props: PropsComponent | void = Batten.getProperties(material);

    if (!props) return 0;

    return computeWeightWithDensity(
      customPiece.realWidth,
      customPiece.realHeight,
      customPiece.realDepth,
      props.density,
    );
  }
}
